// The Setup form's pure layer: stored Finance Config ↔ form state ↔ native
// config to save. The form edits the keys it models and preserves every
// other key at every level (spec call 1), so `toNative(fromConfig(raw))` is
// deep-equal to `raw` for a native config.
import normalizeConfig from "../finance/normalizeConfig";
import { regionOf, invoicingOf, REGIONS, RATE_CARDS } from "../finance/presets";

const clone = v => JSON.parse(JSON.stringify(v));
const DEFAULTS = { currency: "NZD", tax: "gst", ledgerLookAhead: 0 };

export const MODULE_LABELS = { retainer: "Retainer", recurringSessions: "Coaching", drawdownPool: "Draw-down fund", rateCard: "Rate card", sessionBlock: "Session block" };
export const MODULE_ORDER = ["retainer", "recurringSessions", "drawdownPool", "rateCard", "sessionBlock"];

// form = { raw, legacy, top: { engagementType, engagementStart, currency, tax, ledgerLookAhead }, modules: {...full module objects...} }
export function fromConfig(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const legacy = !(r.modules && typeof r.modules === "object");
  const n = normalizeConfig(legacy ? r : null);
  return {
    raw: clone(r),
    legacy,
    top: {
      engagementType: r.engagementType || "",
      engagementStart: r.engagementStart || "",
      currency: r.currency || DEFAULTS.currency,
      tax: r.tax || DEFAULTS.tax,
      ledgerLookAhead: r.ledgerLookAhead === undefined ? DEFAULTS.ledgerLookAhead : Number(r.ledgerLookAhead) || 0,
      region: regionOf(r),
      invoicing: invoicingOf(r),
    },
    modules: clone(legacy ? n.modules : r.modules),
    // Invoice defaults (top-level `invoicing`), read by the USD generator.
    invoicing: { clientShort: r.invoicing?.clientShort || "", billTo: { name: "", address: "", email: "", ...(r.invoicing?.billTo || {}) } },
  };
}

export function toNative(form) {
  const out = form.legacy ? {} : clone(form.raw);
  const had = k => !form.legacy && form.raw[k] !== undefined;
  const put = (k, v, def) => { if (v !== "" && v !== null && v !== undefined && (had(k) || v !== def)) out[k] = v; else if (!had(k)) delete out[k]; else delete out[k]; };
  // Type and start are always written when set
  if (form.top.engagementType) out.engagementType = form.top.engagementType; else delete out.engagementType;
  if (form.top.engagementStart) out.engagementStart = form.top.engagementStart; else delete out.engagementStart;
  put("currency", form.top.currency, DEFAULTS.currency);
  put("tax", form.top.tax, DEFAULTS.tax);
  put("ledgerLookAhead", form.top.ledgerLookAhead, DEFAULTS.ledgerLookAhead);
  // billing: written when the stored config carried it, or when the form's
  // choice differs from what currency alone would imply.
  const implied = { region: regionOf({ currency: out.currency }), invoicing: REGIONS[regionOf({ currency: out.currency })].invoicing };
  if (had("billing") || form.top.region !== implied.region || form.top.invoicing !== implied.invoicing) out.billing = { ...(had("billing") ? form.raw.billing : {}), region: form.top.region, invoicing: form.top.invoicing };
  else delete out.billing;
  out.modules = clone(form.modules);
  const inv = form.invoicing || {};
  const bt = Object.fromEntries(Object.entries(inv.billTo || {}).filter(([, v]) => String(v || "").trim() !== ""));
  const invOut = { ...(form.legacy ? {} : form.raw.invoicing || {}) };
  if (inv.clientShort && inv.clientShort.trim()) invOut.clientShort = inv.clientShort.trim(); else delete invOut.clientShort;
  if (Object.keys(bt).length) invOut.billTo = bt; else delete invOut.billTo;
  if (Object.keys(invOut).length) out.invoicing = invOut; else delete out.invoicing;
  return out;
}

// Form-level required fields; validateConfig stays as it is (spec call 3).
export function requiredProblems(form) {
  const p = [];
  const m = form.modules || {};
  if (m.retainer) {
    if (!String(m.retainer.label || "").trim()) p.push("Retainer needs a label");
    if (!(Number(m.retainer.monthlyTotal) > 0)) p.push("Retainer needs a monthly total above zero");
  }
  if (m.drawdownPool && !Number.isFinite(Number(m.drawdownPool.monthlyAccrual)) ) p.push("Draw-down fund needs a monthly accrual (zero is fine)");
  if (m.drawdownPool && (m.drawdownPool.monthlyAccrual === undefined || m.drawdownPool.monthlyAccrual === "")) { if (!p.includes("Draw-down fund needs a monthly accrual (zero is fine)")) p.push("Draw-down fund needs a monthly accrual (zero is fine)"); }
  (m.rateCard?.rates || []).forEach((r, i) => {
    if (!String(r.label || "").trim()) p.push(`Rate ${i + 1} needs a label`);
    if (!Number.isFinite(Number(r.rate)) || r.rate === "" || r.rate === undefined) p.push(`Rate ${i + 1} needs a rate`);
  });
  if (m.sessionBlock && !(Number(m.sessionBlock.purchased) >= 1)) p.push("Session block needs at least one purchased session");
  return p;
}

// Bodies a module starts with when switched on. Never a shape that renders
// as a finished thing ("$0/mo" is caught by requiredProblems until filled).
export function seed(module, today, currency) {
  switch (module) {
    case "retainer": return { label: "", amount: 0, includes: [], monthlyTotal: 0 };
    case "recurringSessions": return { sessions: [], rolloverValue: 0 };
    case "drawdownPool": return { monthlyAccrual: 0 };
    case "rateCard": return { rates: clone(RATE_CARDS[currency] || []) };
    case "sessionBlock": return { label: "Coaching block", purchased: 6, blockNo: 1, opened: today || new Date().toLocaleDateString("en-CA") };
    default: return {};
  }
}

export const slugId = s => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "person";

// Ids are immutable once created (spec call 2); a new one avoids collisions.
export function newRosterId(name, existing) {
  const base = slugId(name);
  const taken = new Set((existing || []).map(e => e.id));
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

// "Start new block" (spec call 4): archive the current block's sessions and
// funding into history, open block n+1 today with no invoices.
export function startNewBlock(rawConfig, blockData, today) {
  const form = fromConfig(rawConfig);
  const cur = form.modules.sessionBlock || {};
  const sessions = (blockData?.sessions || []).filter(s => s.block === undefined || s.block === null || s.block === cur.blockNo);
  const kept = (blockData?.sessions || []).filter(s => !sessions.includes(s));
  const entry = { blockNo: cur.blockNo || 1, purchased: cur.purchased || 0, label: cur.label, opened: cur.opened || "", closed: today, sessions };
  if (cur.value !== undefined) entry.value = cur.value;
  if (cur.rate !== undefined) entry.rate = cur.rate;
  if (Array.isArray(cur.invoices)) entry.invoices = cur.invoices;
  const next = { ...cur, blockNo: (cur.blockNo || 1) + 1, opened: today };
  delete next.invoices; delete next.invoice; delete next.period;
  form.modules.sessionBlock = next;
  return { config: toNative(form), data: { sessions: kept, history: [...(blockData?.history || []), entry] } };
}
